import React from "react";
import { Box } from "@mui/material";
import { keyframes } from "@mui/system";
import { alpha } from "@mui/material/styles";
import variables from "theme/styles/_variables.module.scss";

type ChartLineSkeletonProps = {
  minHeight?: number;
};

const pulse = keyframes`
  0% { opacity: 0.3; }
  50% { opacity: 0.5; }
  100% { opacity: 0.3; }
`;

const shimmerLegend = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const ChartLineSkeleton: React.FC<ChartLineSkeletonProps> = (props) => {
  const minHeight = props.minHeight ?? 200;

  return (
    <Box sx={{ position: "relative", width: "100%", height: minHeight, px: 2, py: 1 }}>
      {/* Grid lines */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage: (theme) =>
            `repeating-linear-gradient(to bottom, ${alpha(theme.palette.text.primary, 0.08)} 0, ${alpha(
              theme.palette.text.primary,
              0.08
            )} 1px, transparent 1px, transparent 48px)`,
        }}
      />

      {/* Chart area with wavy lines */}
      <Box sx={{ position: "relative", width: "100%", height: "calc(100% - 40px)" }}>
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 300 100"
          preserveAspectRatio="none"
          style={{ position: "absolute", top: 0, left: 0 }}
        >
          {/* First animated wavy line */}
          <path fill="none" stroke={variables.skeletonPaper} strokeWidth="2" strokeLinecap="round">
            <animate
              attributeName="d"
              values="
                M 0 45 Q 30 35, 60 45 T 120 45 Q 150 35, 180 45 T 240 45 Q 270 35, 300 45;
                M 0 45 Q 30 55, 60 45 T 120 45 Q 150 55, 180 45 T 240 45 Q 270 55, 300 45;
                M 0 45 Q 30 35, 60 45 T 120 45 Q 150 35, 180 45 T 240 45 Q 270 35, 300 45"
              dur="2s"
              repeatCount="indefinite"
            />
          </path>

          {/* Second animated wavy line */}
          <path fill="none" stroke={variables.skeletonPaper} strokeWidth="2" strokeLinecap="round">
            <animate
              attributeName="d"
              values="
                M 0 65 Q 25 60, 50 65 T 100 65 Q 125 60, 150 65 T 200 65 Q 225 60, 250 65 T 300 65;
                M 0 65 Q 25 70, 50 65 T 100 65 Q 125 70, 150 65 T 200 65 Q 225 70, 250 65 T 300 65;
                M 0 65 Q 25 60, 50 65 T 100 65 Q 125 60, 150 65 T 200 65 Q 225 60, 250 65 T 300 65"
              dur="2.5s"
              repeatCount="indefinite"
            />
          </path>

          {/* Optional shimmer overlay for first line */}
          <defs>
            <linearGradient id="shimmer" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={variables.lightGray} stopOpacity={0} />
              <stop offset="50%" stopColor={variables.lightGray} stopOpacity={0.6} />
              <stop offset="100%" stopColor={variables.lightGray} stopOpacity={0} />
              <animateTransform
                attributeName="transform"
                type="translate"
                from="-1 0"
                to="1 0"
                dur="3s"
                repeatCount="indefinite"
              />
            </linearGradient>
          </defs>

          <path fill="none" stroke="url(#shimmer)" strokeWidth="3" strokeLinecap="round" opacity="0.5">
            <animate
              attributeName="d"
              values="
                M 0 45 Q 30 35, 60 45 T 120 45 Q 150 35, 180 45 T 240 45 Q 270 35, 300 45;
                M 0 45 Q 30 55, 60 45 T 120 45 Q 150 55, 180 45 T 240 45 Q 270 55, 300 45;
                M 0 45 Q 30 35, 60 45 T 120 45 Q 150 35, 180 45 T 240 45 Q 270 35, 300 45"
              dur="2s"
              repeatCount="indefinite"
            />
          </path>
        </svg>

        {/* X-axis labels */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "space-between",
            px: 1,
          }}
        >
          {[1, 2, 3, 4, 5, 6].map((idx) => (
            <Box
              key={idx}
              sx={{
                width: 30,
                height: 8,
                borderRadius: 1,
                backgroundColor: variables.skeletonPaper,
                animation: `${pulse} 2s ease-in-out ${idx * 0.1}s infinite`,
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Legend */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          justifyContent: "center",
          mt: 1,
          px: 2,
        }}
      >
        {[1, 2].map((idx) => (
          <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: variables.skeletonPaper,
                animation: `${pulse} 2s ease-in-out ${idx * 0.3}s infinite`,
              }}
            />
            <Box
              sx={{
                position: "relative",
                width: 60,
                height: 10,
                borderRadius: 1,
                backgroundColor: variables.skeletonPaper,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(90deg, 
                    transparent 0%, 
                    ${alpha(variables.lightGray, 0.6)} 50%, 
                    transparent 100%)`,
                  animation: `${shimmerLegend} 2.5s ease-in-out ${idx * 0.2}s infinite`,
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ChartLineSkeleton;
